import { useEffect, useRef, useState } from 'react';

import { FileUpload, type UploadResult } from '@/components/FileUpload';
import { Tooltip } from '@/components/Tooltip/Tooltip';
import { useGetDynamicImageUrl } from '@/components/useGetDynamicImageUrl';

import { AltTextField } from './AltTextField';
import styles from './ImageUnitEditor.module.css';

function RemoveIcon() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="16"
      viewBox="0 0 24 24"
      width="16"
    >
      <path
        d="m6 6 12 12M18 6 6 18"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

/** What a new image is still waiting for: its upload, or its resizable copy. */
export type ImageWait = 'copy' | 'upload';

/**
 * How long a new image waits for its resizable copy. A copy that has not come
 * by then is taken as failed, and the unit saves at full size, said on
 * screen: while it waits, nothing but Cancel or Remove image lets it go.
 */
const COPY_TIME_LIMIT_MS = 20_000;

interface ImageUnitEditorProps {
  altText?: string;
  dynamicImageUrl?: string;
  imageUrl?: string;
  validationError?: string;
  onChange: (
    updates: Partial<{
      altText?: string;
      dynamicImageUrl?: string;
      imageUrl?: string;
    }>,
  ) => void;
  /**
   * Told whenever what the image waits for changes, so that its unit is not
   * saved before it is ready: a unit saved mid-upload had no image, and one
   * saved before its resizable copy came went without it for good.
   */
  onWaitChange?: (wait: ImageWait | null) => void;
}

export function ImageUnitEditor({
  altText,
  dynamicImageUrl,
  imageUrl,
  onChange,
  onWaitChange,
  validationError,
}: ImageUnitEditorProps) {
  const { getDynamicImageUrl } = useGetDynamicImageUrl();
  const [uploadsUnderway, setUploadsUnderway] = useState(0);
  // Each new image, uploaded or removed, takes a turn, and its copy answers
  // only for its own: the copy of an image already removed used to end the
  // wait for the next, and put the removed image back.
  const imageTurnRef = useRef(0);
  const [copyTurn, setCopyTurn] = useState<number | null>(null);
  const [copyFailed, setCopyFailed] = useState(false);
  const previewSrc = dynamicImageUrl?.trim() || imageUrl?.trim() || '';
  const wait: ImageWait | null =
    uploadsUnderway > 0 ? 'upload' : copyTurn !== null ? 'copy' : null;

  useEffect(() => {
    onWaitChange?.(wait);
  }, [onWaitChange, wait]);

  const handleUploadComplete = async (result: UploadResult) => {
    imageTurnRef.current += 1;
    const turn = imageTurnRef.current;

    setCopyFailed(false);
    setCopyTurn(turn);
    onChange({
      dynamicImageUrl: undefined,
      imageUrl: result.downloadUrl,
    });

    let resolvedDynamicImageUrl: string | null = null;
    let failed = false;
    let timeLimit = 0;

    try {
      resolvedDynamicImageUrl = await Promise.race([
        getDynamicImageUrl(result.storagePath),
        new Promise<never>((_resolve, reject) => {
          timeLimit = window.setTimeout(() => {
            reject(new Error('The resizable copy did not come in time.'));
          }, COPY_TIME_LIMIT_MS);
        }),
      ]);
    } catch {
      failed = true;
    } finally {
      window.clearTimeout(timeLimit);
    }

    if (imageTurnRef.current !== turn) {
      return;
    }

    setCopyTurn(null);
    setCopyFailed(failed);
    onChange({
      dynamicImageUrl: resolvedDynamicImageUrl ?? undefined,
      imageUrl: result.downloadUrl,
    });
  };

  return (
    <div className={styles.root} data-invalid={validationError ? 'true' : 'false'}>
      {!previewSrc ? (
        <div className={styles.uploadSection}>
          <p className={styles.sectionLabel}>Upload image</p>
          <FileUpload
            accept="image/*"
            buttonLabel="Upload Image"
            multiple={false}
            onUploadComplete={(result) => {
              void handleUploadComplete(result);
            }}
            onUploadEnd={() => {
              setUploadsUnderway((count) => Math.max(0, count - 1));
            }}
            onUploadStart={() => {
              setUploadsUnderway((count) => count + 1);
            }}
            storagePath="images/uploads"
          />
        </div>
      ) : null}

      <div className={styles.preview}>
        <p className={styles.previewLabel}>Preview</p>
        <div className={styles.previewSurface}>
          {previewSrc ? (
            <div className={styles.previewImageFrame}>
              <Tooltip content="Remove image">
                <button
                  aria-label="Remove image"
                  className={styles.removeButton}
                  onClick={() => {
                    imageTurnRef.current += 1;
                    setCopyTurn(null);
                    setCopyFailed(false);
                    onChange({
                      dynamicImageUrl: undefined,
                      imageUrl: undefined,
                    });
                  }}
                  type="button"
                >
                  <RemoveIcon />
                </button>
              </Tooltip>
              {/* Named by the alt text below as it is written, which is what a
                  student's screen reader will say; with none, it is decorative. */}
              <img alt={altText?.trim() ?? ''} className={styles.previewImage} src={previewSrc} />
            </div>
          ) : (
            <span className={styles.placeholder}>Upload an image to continue.</span>
          )}
        </div>
        {validationError ? (
          <p className={styles.validationMessage}>{validationError}</p>
        ) : null}
        {copyTurn !== null ? (
          <p className={styles.statusMessage}>Preparing the image…</p>
        ) : null}
        {copyFailed ? (
          <p className={styles.errorMessage}>
            The image could not be prepared for every screen size. It will show at
            full size.
          </p>
        ) : null}
      </div>

      {/* Under the preview on purpose: an instructor writes a better description
          while looking at the image than before choosing one. */}
      {previewSrc ? (
        <AltTextField
          onChange={(nextAltText) => {
            onChange({ altText: nextAltText });
          }}
          value={altText}
        />
      ) : null}
    </div>
  );
}
